import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import {
  Home,
  Hub,
  Warning,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';

const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['transform', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const IconContainer = styled(Box)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  width: 48,
  height: 48,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor,
  color: 'white',
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  minHeight: 400,
  display: 'flex',
  flexDirection: 'column',
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
}));

const NoDataText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1rem',
  textAlign: 'center',
  marginTop: theme.spacing(4),
}));

const ChartContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: 32,
});

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const LegendItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const LegendColor = styled(Box)<{ color: string }>(({ color }) => ({
  width: 16,
  height: 16,
  borderRadius: '4px',
  backgroundColor: color,
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

interface DashboardDemoProps {
  classes?: any;
}

class DashboardDemo extends Component<DashboardDemoProps> {
  static propTypes = {
    classes: PropTypes.object,
  };

  render() {
    // Summary data
    const summaryData = [
      {
        title: 'Total Homes',
        value: '1',
        icon: <Home />,
        iconColor: '#9C27B0', // Purple
      },
      {
        title: 'Total Assets',
        value: '0',
        icon: <Hub />,
        iconColor: '#FFC107', // Yellow
      },
      {
        title: 'Total Issues',
        value: '6',
        icon: <Warning />,
        iconColor: '#F44336', // Red
      },
    ];

    // Issues chart data
    const issuesData = [
      { name: 'Flagged', value: 2, color: '#F44336' }, // Red
      { name: 'Resolved', value: 3, color: '#009688' }, // Teal
      { name: 'WIP', value: 1, color: '#2196F3' }, // Blue
    ];

    // Assets chart data (empty)
    const assetsData: any[] = [];

    return (
      <DashboardContainer>
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid size={{xs:12,sm:4}}>
            <SummaryCard>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    marginBottom: 1,
                  }}
                >
                  {summaryData[0].title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  {summaryData[0].value}
                </Typography>
              </Box>
              <IconContainer bgcolor={summaryData[0].iconColor}>
                {summaryData[0].icon}
              </IconContainer>
            </SummaryCard>
          </Grid>

          <Grid size={{xs:12,sm:4}}>
            <SummaryCard>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    marginBottom: 1,
                  }}
                >
                  {summaryData[1].title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  {summaryData[1].value}
                </Typography>
              </Box>
              <IconContainer bgcolor={summaryData[1].iconColor}>
                {summaryData[1].icon}
              </IconContainer>
            </SummaryCard>
          </Grid>

          <Grid size={{xs:12,sm:4}}>
            <SummaryCard>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    marginBottom: 1,
                  }}
                >
                  {summaryData[2].title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  {summaryData[2].value}
                </Typography>
              </Box>
              <IconContainer bgcolor={summaryData[2].iconColor}>
                {summaryData[2].icon}
              </IconContainer>
            </SummaryCard>
          </Grid>

          {/* Assets Chart */}
          <Grid size={{xs:12,sm:6}}>
            <ChartCard>
              <ChartTitle>Assets</ChartTitle>
              {assetsData.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[{ name: 'Empty', value: 100 }]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        fill="#424242"
                      >
                        <Cell fill="#424242" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <NoDataText>No Data</NoDataText>
                </Box>
              ) : (
                <ChartContainer>
                  <ResponsiveContainer width="60%" height={250}>
                    <PieChart>
                      <Pie
                        data={assetsData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                      >
                        {assetsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              )}
            </ChartCard>
          </Grid>

          {/* Issues Chart */}
          <Grid size={{xs:12,sm:6}}>
            <ChartCard>
              <ChartTitle>Issues</ChartTitle>
              <ChartContainer>
                <ResponsiveContainer width="60%" height={250}>
                  <PieChart>
                    <Pie
                      data={issuesData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {issuesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <LegendContainer>
                  {issuesData.map((item, index) => (
                    <LegendItem key={index}>
                      <LegendColor color={item.color} />
                      <LegendText>{item.name}</LegendText>
                    </LegendItem>
                  ))}
                </LegendContainer>
              </ChartContainer>
            </ChartCard>
          </Grid>
        </Grid>
      </DashboardContainer>
    );
  }
}

export default DashboardDemo;

